import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Plane, CheckCircle, Clock, Shield, MapPin, Phone } from 'lucide-react';

const TaxiAeroportBruxelles = () => {
  return (
    <>
      <Helmet>
        <title>Taxi Aéroport Bruxelles | Transfert Zaventem & Charleroi | 24/7</title>
        <meta name="description" content="Taxi aéroport Bruxelles vers Zaventem et Charleroi. Service 24/7, prix fixes, prise en charge rapide. Réservation en ligne. Chauffeurs professionnels." />
        <meta name="keywords" content="taxi aeroport bruxelles, transfert aeroport bruxelles, taxi zaventem, taxi charleroi airport, navette aeroport" />
        <link rel="canonical" href="https://www.luxurytransferservices.be/taxi-aeroport-bruxelles" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "TaxiService",
            "name": "Taxi Aéroport Bruxelles",
            "description": "Service de taxi vers les aéroports de Bruxelles",
            "telephone": "+32-2-315-02-54",
            "priceRange": "€€€"
          })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black">
        <Navbar />

        <section className="relative pt-32 pb-20 px-4">
          <div className="container mx-auto max-w-6xl">
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                Taxi Aéroport <span className="text-gold">Bruxelles</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-8">
                Transferts professionnels vers/depuis Zaventem & Charleroi - Disponible 24h/24
              </p>
              <Button size="lg" className="bg-gold hover:bg-amber-600 text-black text-lg px-8">
                <Phone className="w-5 h-5 mr-2" />
                Réserver Mon Transfert
              </Button>
            </div>
          </div>
        </section>

        <section className="py-16 px-4">
          <div className="container mx-auto max-w-6xl">
            <h2 className="text-3xl font-bold text-white mb-6">
              Service de Taxi Professionnel vers les Aéroports de Bruxelles
            </h2>
            <p className="text-lg text-gray-300 mb-8">
              Besoin d'un <strong>taxi pour l'aéroport de Bruxelles</strong> ? Notre service premium vous garantit 
              un <strong>transfert aéroport fiable et ponctuel</strong> vers Zaventem et Charleroi Airport. 
              Que ce soit pour un vol tôt le matin ou une arrivée tardive, nous sommes disponibles 24h/24 pour vos 
              <strong>transferts aéroport Bruxelles</strong>.
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-16">
              <Card className="bg-gray-800/50 border-gold/20">
                <CardContent className="p-6">
                  <Plane className="w-12 h-12 text-gold mb-4" />
                  <h3 className="text-2xl font-bold text-white mb-3">Taxi Zaventem</h3>
                  <p className="text-gray-300 mb-4">
                    Transfert rapide et confortable vers/depuis l'aéroport Brussels Airport (BRU). 
                    Service de prise en charge au terminal avec suivi de vol.
                  </p>
                  <Link to="/transfert-zaventem-bruxelles">
                    <Button variant="outline" className="border-gold/30 text-gold">
                      Plus d'infos →
                    </Button>
                  </Link>
                </CardContent>
              </Card>

              <Card className="bg-gray-800/50 border-gold/20">
                <CardContent className="p-6">
                  <Plane className="w-12 h-12 text-gold mb-4" />
                  <h3 className="text-2xl font-bold text-white mb-3">Taxi Charleroi Airport</h3>
                  <p className="text-gray-300 mb-4">
                    Transfert vers Brussels South Charleroi Airport (CRL). Prise en charge rapide pour 
                    vols Ryanair et autres compagnies low-cost.
                  </p>
                  <Link to="/taxi-charleroi-bruxelles">
                    <Button variant="outline" className="border-gold/30 text-gold">
                      Plus d'infos →
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>

            <h2 className="text-3xl font-bold text-white mb-6">
              Pourquoi Choisir Notre Taxi Aéroport ?
            </h2>
            <div className="grid md:grid-cols-3 gap-6 mb-16">
              <div className="text-center">
                <Clock className="w-16 h-16 text-gold mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Ponctualité Garantie</h3>
                <p className="text-gray-400">Suivi de vol en temps réel pour les arrivées</p>
              </div>
              <div className="text-center">
                <Shield className="w-16 h-16 text-gold mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Prix Fixes</h3>
                <p className="text-gray-400">Pas de mauvaise surprise au compteur</p>
              </div>
              <div className="text-center">
                <CheckCircle className="w-16 h-16 text-gold mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-white mb-2">Service 24/7</h3>
                <p className="text-gray-400">Disponible jour et nuit</p>
              </div>
            </div>

            <div className="bg-gold/10 border border-gold/30 rounded-lg p-8 text-center">
              <h2 className="text-2xl font-bold text-white mb-4">
                Réservez Votre Taxi Aéroport Maintenant
              </h2>
              <p className="text-gray-300 mb-6">
                Transferts garantis vers Zaventem et Charleroi - Confirmation immédiate
              </p>
              <Button size="lg" className="bg-gold hover:bg-amber-600 text-black">
                Réserver En Ligne
              </Button>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default TaxiAeroportBruxelles;